import React, { useState, useEffect } from 'react';
import { Plus, Download, Trash2, AlertTriangle } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/AuthContext';
import DataGrid from '../../components/DataGrid';
import { StatTile, MiniBarChart } from '../../components/charts/MiniCharts';
import {
  ledger as ledgerApi,
  reports as reportsApi,
  reportSchedules as scheduleApi,
  type BalanceSheet,
  type FinancialReport,
  type CollectionReport,
  type Defaulter,
  type SlaComplianceReport,
  type SlaCategoryCompliance,
  type FacilityUtilizationReport,
  type FacilityUsage,
  type ReportSchedule,
  type ApiError,
} from '../../services/api';
import { downloadBlob } from '../../utils/downloadBlob';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

type Tab = 'financial' | 'collection' | 'sla' | 'facility' | 'export' | 'scheduled';

const fmtAmount = (n: number | undefined) =>
  `₹${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;


const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const [tab, setTab] = useState<Tab>('financial');

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Reports
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Financial, collection, SLA and facility reports with PDF/CSV export.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {([
          ['financial', 'Financial'], ['collection', 'Collection & Defaulters'], ['sla', 'SLA Compliance'],
          ['facility', 'Facility Utilisation'], ['export', 'Export'], ['scheduled', 'Scheduled Reports'],
        ] as [Tab, string][]).map(([t, label]) => (
          <button
            key={t} onClick={() => setTab(t)}
            className={tab === t ? 'nx-btn nx-btn-primary nx-btn-sm' : 'nx-btn nx-btn-outline nx-btn-sm'}
          >
            {label}
          </button>
        ))}
      </div>

      {!societyId ? null : tab === 'financial' ? <FinancialTab societyId={societyId} />
        : tab === 'collection' ? <CollectionTab societyId={societyId} />
        : tab === 'sla' ? <SlaTab societyId={societyId} />
        : tab === 'facility' ? <FacilityTab societyId={societyId} />
        : tab === 'export' ? <ExportTab societyId={societyId} />
        : <ScheduledTab societyId={societyId} />}
    </PageLayout>
  );
};

const FinancialTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [report, setReport] = useState<FinancialReport | null>(null);
  const [balance, setBalance] = useState<BalanceSheet | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([reportsApi.financial(societyId), ledgerApi.getBalanceSheet(societyId)])
      .then(([r, b]) => { setReport(r); setBalance(b); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [societyId]);

  if (loading) return <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Loading…</p>;

  return (
    <>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatTile label="Cash on Hand" value={fmtAmount(balance?.cashOnHand)} accent="#10B981" />
        <StatTile label="Dues Receivable" value={fmtAmount(balance?.duesReceivable)} accent="#D97706" />
        <StatTile label="Fund Balance" value={fmtAmount(balance?.totalFundBalance)} accent="#6D28D9" />
        <StatTile label="Net Surplus" value={fmtAmount(report?.netSurplus)} accent="#1A6CF6" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="nx-card" style={{ padding: 20 }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Income by Month</h3>
          <MiniBarChart data={(report?.incomeByMonth ?? []).map(m => ({ label: m.month, value: m.amount }))} color="#10B981" formatValue={fmtAmount} />
        </div>
        <div className="nx-card" style={{ padding: 20 }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Expenses by Category</h3>
          <MiniBarChart data={(report?.expensesByCategory ?? []).map(c => ({ label: c.category, value: c.amount }))} color="#DC2626" formatValue={fmtAmount} />
        </div>
      </div>
    </>
  );
};

const CollectionTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [report, setReport] = useState<CollectionReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportsApi.collection(societyId).then(setReport).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  return (
    <>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatTile label="Collection Rate" value={`${(report?.collectionRate ?? 0).toFixed(1)}%`} accent="#10B981" />
        <StatTile label="Total Billed" value={fmtAmount(report?.totalBilled)} />
        <StatTile label="Total Collected" value={fmtAmount(report?.totalCollected)} accent="#1A6CF6" />
        <StatTile label="Defaulters" value={String(report?.defaulters.length ?? 0)} accent="#DC2626" />
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'unit', header: 'Unit', cellStyle: { fontWeight: 500 }, render: (d: Defaulter) => d.unitNumber || '—' },
            { key: 'resident', header: 'Resident', render: (d: Defaulter) => d.residentName || '—' },
            { key: 'amount', header: 'Outstanding', render: (d: Defaulter) => fmtAmount(d.outstandingAmount) },
            { key: 'overdue', header: 'Days Overdue', render: (d: Defaulter) => String(d.daysOverdue) },
            { key: 'due', header: 'Due Date', render: (d: Defaulter) => d.dueDate || '—' },
          ]}
          rows={report?.defaulters ?? []}
          rowKey={d => d.unitId + d.dueDate}
          loading={loading}
          emptyIcon={<AlertTriangle size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No defaulters — full collection."
        />
      </div>
    </>
  );
};

const SlaTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [report, setReport] = useState<SlaComplianceReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportsApi.slaCompliance(societyId).then(setReport).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  return (
    <>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatTile label="Overall Compliance" value={`${(report?.overallComplianceRate ?? 0).toFixed(1)}%`} accent="#10B981" />
        <StatTile label="Total Tickets" value={String(report?.totalTickets ?? 0)} />
        <StatTile label="Breached" value={String(report?.totalBreached ?? 0)} accent="#DC2626" />
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'category', header: 'Category', cellStyle: { fontWeight: 500 }, render: (c: SlaCategoryCompliance) => c.category },
            { key: 'total', header: 'Total', render: (c: SlaCategoryCompliance) => String(c.total) },
            { key: 'breached', header: 'Breached', render: (c: SlaCategoryCompliance) => String(c.breached) },
            { key: 'compliance', header: 'Compliance %', render: (c: SlaCategoryCompliance) => `${c.complianceRate.toFixed(1)}%` },
            { key: 'avg', header: 'Avg Resolution (hrs)', render: (c: SlaCategoryCompliance) => c.avgResolutionHours != null ? c.avgResolutionHours.toFixed(1) : '—' },
          ]}
          rows={report?.categories ?? []}
          rowKey={c => c.category}
          loading={loading}
          emptyText="No tickets yet."
        />
      </div>
    </>
  );
};

const FacilityTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [report, setReport] = useState<FacilityUtilizationReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportsApi.facilityUtilization(societyId).then(setReport).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  return (
    <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
      <DataGrid
        columns={[
          { key: 'name', header: 'Facility', cellStyle: { fontWeight: 500 }, render: (f: FacilityUsage) => f.facilityName },
          { key: 'bookings', header: 'Total Bookings', render: (f: FacilityUsage) => String(f.totalBookings) },
          { key: 'utilization', header: 'Utilisation %', render: (f: FacilityUsage) => `${f.utilizationRate.toFixed(1)}%` },
          { key: 'revenue', header: 'Revenue', render: (f: FacilityUsage) => fmtAmount(f.revenue) },
        ]}
        rows={report?.facilities ?? []}
        rowKey={f => f.facilityId}
        loading={loading}
        emptyText="No facilities booked in the last 30 days."
      />
    </div>
  );
};

const REPORT_TYPES: { value: 'financial' | 'collection' | 'sla' | 'facility'; label: string }[] = [
  { value: 'financial', label: 'Financial (P&L)' },
  { value: 'collection', label: 'Collection & Defaulters' },
  { value: 'sla', label: 'SLA Compliance' },
  { value: 'facility', label: 'Facility Utilisation' },
];

const ExportTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [type, setType] = useState<'financial' | 'collection' | 'sla' | 'facility'>('financial');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const exportReport = async (format: 'pdf' | 'csv') => {
    setExporting(true); setError('');
    try {
      const blob = await reportsApi.exportBlob(societyId, type, format);
      downloadBlob(blob, `${type}-report.${format}`);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to export report');
    } finally { setExporting(false); }
  };

  return (
    <div className="nx-card" style={{ padding: 24, maxWidth: 480 }}>
      <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Report Builder & Export</h3>
      {error && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: 12, color: '#EF4444', marginBottom: 14 }}>{error}</div>
      )}
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Report Type</label>
        <select value={type} onChange={e => setType(e.target.value as typeof type)} style={inp}>
          {REPORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="nx-btn nx-btn-primary" disabled={exporting} onClick={() => exportReport('pdf')} style={{ gap: 6 }}>
          <Download size={14} /> Export PDF
        </button>
        <button className="nx-btn nx-btn-outline" disabled={exporting} onClick={() => exportReport('csv')} style={{ gap: 6 }}>
          <Download size={14} /> Export CSV
        </button>
      </div>
    </div>
  );
};

const ScheduledTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [items, setItems] = useState<ReportSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ reportType: 'financial' as ReportSchedule['reportType'], frequency: 'MONTHLY' as ReportSchedule['frequency'], recipientEmails: '' });

  const refresh = () => {
    scheduleApi.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(refresh, [societyId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.recipientEmails.trim()) return;
    setSaving(true); setError('');
    try {
      await scheduleApi.create(societyId, form);
      setShowForm(false); setForm({ reportType: 'financial', frequency: 'MONTHLY', recipientEmails: '' });
      refresh();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to create schedule');
    } finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    await scheduleApi.delete(societyId, id).catch(() => {});
    refresh();
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Schedule Report
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'type', header: 'Report', cellStyle: { fontWeight: 500 }, render: (s: ReportSchedule) => REPORT_TYPES.find(t => t.value === s.reportType)?.label ?? s.reportType },
            { key: 'frequency', header: 'Frequency', render: (s: ReportSchedule) => s.frequency },
            { key: 'recipients', header: 'Recipients', render: (s: ReportSchedule) => s.recipientEmails },
            { key: 'lastSent', header: 'Last Sent', render: (s: ReportSchedule) => s.lastSentAt ? new Date(s.lastSentAt).toLocaleString() : 'Never' },
            {
              key: 'actions', header: '',
              render: (s: ReportSchedule) => (
                <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => remove(s.id)} style={{ gap: 4 }}>
                  <Trash2 size={12} /> Remove
                </button>
              ),
            },
          ]}
          rows={items}
          rowKey={s => s.id}
          loading={loading}
          emptyText="No scheduled reports yet."
        />
      </div>
      {showForm && (
        <Modal title="Schedule Report" submitLabel="Schedule" error={error} isLoading={saving} onClose={() => setShowForm(false)} onSubmit={submit}>
          <div>
            <label style={lbl}>Report Type</label>
            <select value={form.reportType} onChange={e => setForm(p => ({ ...p, reportType: e.target.value as ReportSchedule['reportType'] }))} style={inp}>
              {REPORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Frequency</label>
            <select value={form.frequency} onChange={e => setForm(p => ({ ...p, frequency: e.target.value as ReportSchedule['frequency'] }))} style={inp}>
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Recipient Emails (comma-separated) *</label>
            <input value={form.recipientEmails} onChange={e => setForm(p => ({ ...p, recipientEmails: e.target.value }))} placeholder="admin@society.com, treasurer@society.com" required style={inp} />
          </div>
        </Modal>
      )}
    </>
  );
};

export default ReportsPage;
